import { useCallback, useMemo } from "react";

import Box from "@mui/material/Box";
import Button, { ButtonProps } from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { error } from "app/themes/colors";
import Dialog from "components/dialog";

import {
  resetConfirmDialog,
  selectConfirmDialogContent,
  selectConfirmDialogOnCancel,
  selectConfirmDialogOnChange,
  selectConfirmDialogOpen,
  selectConfirmDialogOptions,
  selectConfirmDialogTitle
} from "./confirm-dialog.slice";

// Exported so surfaces that legitimately cannot use ConfirmDialog itself still get the
// app's danger treatment rather than a hand-rolled near-copy that drifts on the next
// theme change. Today that is RepairDeleteWarningDialog (ENG-1834), which offers three
// actions where this dialog's slice only carries one action + Cancel.
export const DangerButton = styled(Button)<ButtonProps>(({ theme }) => ({
  color: theme.palette.getContrastText(error[600]),
  backgroundColor: error[700],
  "&:hover": {
    backgroundColor: error[800]
  },
  // Without this a disabled danger button stays red and reads as clickable.
  "&.Mui-disabled": {
    color: theme.palette.action.disabled,
    backgroundColor: theme.palette.action.disabledBackground
  }
}));

export interface ConfirmDialogOptions {
  actionButtonType?: string;
  actionButtonText?: string;
  actionButtonDisabled?: boolean;
  hideCancelButton?: boolean;
  zIndex?: number;
  /**
   * A wider dialog for a body that explains a change (a before/after picture, a
   * list). Pixels; never wider than the window less 64px. Unset = the shared
   * Dialog's default 586px minimum, as every other caller gets.
   */
  paperWidth?: number;
  /**
   * Footer button size. Unset = "small", as every other caller gets. Not
   * "large": the theme makes a large button full width (the login screen's).
   */
  buttonSize?: ButtonProps["size"];
}

// The wide dialog's pinned footer: right-aligned like the app's other dialog
// footers, separated from the body, buttons never wrapping their label.
const WIDE_ACTIONS_SX = {
  px: 3,
  py: 2,
  gap: 1.5,
  borderTop: 1,
  borderColor: "divider",
  "& .MuiButton-root": { whiteSpace: "nowrap", px: 3 },
  "& > :not(:first-of-type)": { ml: 0 }
} as const;

export default function ConfirmDialog() {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const confirmChangeDialogOpen = useAppSelector(selectConfirmDialogOpen);
  const onChange = useAppSelector(selectConfirmDialogOnChange);
  const changeTitle = useAppSelector(selectConfirmDialogTitle);
  const changeContent = useAppSelector(selectConfirmDialogContent);
  const onCancel = useAppSelector(selectConfirmDialogOnCancel);
  const options = useAppSelector(selectConfirmDialogOptions);

  const paperWidth = options?.paperWidth;
  const paperSx = useMemo(
    () =>
      paperWidth
        ? // The cap comes from MUI's `lg` paper class below (1200px, or the
          // window less 64px on a narrower one); the shared Dialog's own
          // `maxWidth: 100%` loses to MUI's default 600px `sm` class.
          { width: `${paperWidth}px`, minWidth: 0 }
        : undefined,
    [paperWidth]
  );
  const buttonSize = options?.buttonSize ?? "small";

  const handleChange = useCallback(() => {
    if (onChange) {
      onChange();
    }
    dispatch(resetConfirmDialog({ title: changeTitle }));
  }, [dispatch, onChange, changeTitle]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    dispatch(resetConfirmDialog());
  }, [onCancel, dispatch]);

  const buttons = (
    <>
      {!options?.hideCancelButton && (
        <Button
          data-testid="confirm-dialog-cancel"
          variant="outlined"
          size={buttonSize}
          onClick={handleCancel}
        >
          Cancel
        </Button>
      )}

      {options?.actionButtonType === "danger" ? (
        <Box
          data-testid="confirm-dialog-yes"
          sx={{ ml: 2 }}
          // The click lands on this wrapper even when the button inside is disabled, so
          // a disabled danger action must not run (or close the dialog).
          onClick={options?.actionButtonDisabled ? undefined : handleChange}
        >
          <DangerButton
            size={buttonSize}
            disabled={options?.actionButtonDisabled}
          >
            {options?.actionButtonText ?? "Yes"}
          </DangerButton>
        </Box>
      ) : (
        <Button
          data-testid="confirm-dialog-yes"
          variant="contained"
          size={buttonSize}
          onClick={handleChange}
          disabled={options?.actionButtonDisabled}
        >
          {options?.actionButtonText ?? "Yes"}
        </Button>
      )}
    </>
  );

  return (
    <Dialog
      title={changeTitle || "Are you sure you want to continue?"}
      onClose={handleCancel}
      open={confirmChangeDialogOpen}
      dialogContentProps={{
        sx: {
          display: "flex",
          alignItems: "center",
          pb: "20px",
          flexDirection: "column"
        }
      }}
      sx={{
        zIndex: options?.zIndex ?? theme.zIndex.snackbar + 2
      }}
      paperContentSx={paperSx}
      maxWidth={paperWidth ? "lg" : undefined}
      actions={paperWidth ? buttons : undefined}
      dialogActionsSx={paperWidth ? WIDE_ACTIONS_SX : undefined}
    >
      {changeContent && (
        <Typography component="div" pb={2} variant="body3" width="100%">
          {changeContent}
        </Typography>
      )}
      {/* A wide dialog pins its buttons under the scrolling body, so a long
          explanation never pushes them out of view; every other caller keeps
          them inside the body, as before. */}
      {!paperWidth && <DialogActions>{buttons}</DialogActions>}
    </Dialog>
  );
}
